import { useState } from 'react'
import { IconCategoryGrid } from '../../../components/icons'

export interface CategoryTileImageProps {
  /** `CatalogCategory.imageUrl` — an opaque wire URL/path derived from one of
   * the category's own `CatalogSeries.imageUrl` values, which (like that
   * field) can legitimately be an absolute CDN URL, not just a locally
   * committed root-relative asset. This is `CatalogResultThumb`/`SeriesCard`'s
   * "trust the wire URL, self-heal on error" precedent for `imageUrl` fields
   * — NOT `isSafeCatalogImageRef`, which is specific to `logoRef`'s
   * root-relative-only committed-asset contract and would reject every real
   * `https://...` MISUMI CDN image this field actually carries. */
  readonly imageUrl: string | null
  readonly tintClass: string
  readonly className?: string
  /** Icon size for the fallback glyph only — real images fill their box via
   * CSS, matching each caller's existing tile footprint. */
  readonly iconSize?: number
}

/**
 * A category tile's image (Home carousel / "Shop by category" chip): a real,
 * BFF-derived `imageUrl` when available, falling back to the existing tinted
 * `IconCategoryGrid` decorative glyph — never an empty placeholder box, since
 * the category name is already rendered as visible text beside this element.
 * Mirrors `CatalogResultThumb`'s self-heal pattern for this same class of
 * field (reset on `onError`, and reset again if `imageUrl` itself changes).
 */
export function CategoryTileImage({
  imageUrl,
  tintClass,
  className,
  iconSize,
}: CategoryTileImageProps) {
  const [hasErrored, setHasErrored] = useState(false)
  const [lastImageUrl, setLastImageUrl] = useState(imageUrl)
  if (imageUrl !== lastImageUrl) {
    setLastImageUrl(imageUrl)
    setHasErrored(false)
  }

  if (imageUrl !== null && !hasErrored) {
    return (
      <img
        src={imageUrl}
        alt=""
        aria-hidden="true"
        className={className}
        onError={() => setHasErrored(true)}
      />
    )
  }

  // `tintClass` only applies to the fallback glyph box, not the real `<img>`
  // above — the tint classes (`catalog.css`'s `.catalog-group-tint-0..3`) set
  // a `background-color`, which is desired as the glyph's colored tile
  // background, but was previously ALSO landing on the real `<img>`. That's
  // harmless when the image fully covers its box, but once a caller uses
  // `object-fit: contain` (the Home carousel's small corner box), any
  // letterboxing exposes the tint behind/around the real photo, which reads
  // as a rendering bug, not a placeholder. A real image never needs a tinted
  // background.
  const fallbackClasses = className ? `${className} ${tintClass}` : tintClass

  return (
    <span className={fallbackClasses} aria-hidden="true">
      <IconCategoryGrid {...(iconSize !== undefined ? { size: iconSize } : {})} />
    </span>
  )
}
